/**
 * PageLayout – shared page chrome primitives
 *
 * PageBackground  - dark gradient shell used across all app pages
 * PageSpinner     - fullscreen centered loading state
 * PageEmptyState  - fullscreen centered no-data / error state
 * SettingsToggleBar - "Show all · Hide all" action row for settings dialogs
 */

import type { ReactNode, ComponentType } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '@grt/bodykit';

// ---------------------------------------------------------------------------
// PageBackground
// ---------------------------------------------------------------------------
interface PageBackgroundProps {
  children: ReactNode;
  className?: string;
}

export function PageBackground({ children, className }: PageBackgroundProps) {
  return (
    <div
      className={cn(
        'min-h-screen bg-[rgb(5,5,5)]',
        className
      )}
    >
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// PageSpinner
// ---------------------------------------------------------------------------
interface PageSpinnerProps {
  label?: string;
  className?: string;
}

export function PageSpinner({ label = 'Loading…', className }: PageSpinnerProps) {
  return (
    <PageBackground className={cn('flex items-center justify-center', className)}>
      <div className="text-center">
        <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4 text-primary" />
        <p className="text-zinc-400">{label}</p>
      </div>
    </PageBackground>
  );
}

// ---------------------------------------------------------------------------
// PageEmptyState
// ---------------------------------------------------------------------------
interface PageEmptyStateProps {
  icon?: ComponentType<{ className?: string }>;
  title: string;
  description?: string;
  action?: ReactNode;
  className?: string;
}

export function PageEmptyState({
  icon: Icon,
  title,
  description,
  action,
  className,
}: PageEmptyStateProps) {
  return (
    <PageBackground className={cn('flex items-center justify-center', className)}>
      <div className="text-center p-8">
        {Icon && <Icon className="w-16 h-16 mx-auto mb-4 text-zinc-500" />}
        <h2 className="text-2xl font-semibold mb-2 text-white">{title}</h2>
        {description && <p className="text-zinc-400 mb-6 max-w-md mx-auto">{description}</p>}
        {action}
      </div>
    </PageBackground>
  );
}

// ---------------------------------------------------------------------------
// SettingsToggleBar – "Show all · Hide all" control row for settings dialogs
// ---------------------------------------------------------------------------
interface SettingsToggleBarProps {
  onShowAll: () => void;
  onHideAll: () => void;
}

export function SettingsToggleBar({ onShowAll, onHideAll }: SettingsToggleBarProps) {
  return (
    <div className="flex items-center gap-1 text-xs mb-1">
      <button
        type="button"
        onClick={onShowAll}
        className="px-2 py-1 rounded text-primary hover:bg-primary/10 transition-colors font-medium"
      >
        Show all
      </button>
      <span className="text-zinc-600 select-none">·</span>
      <button
        type="button"
        onClick={onHideAll}
        className="px-2 py-1 rounded text-zinc-400 hover:bg-zinc-700/50 transition-colors"
      >
        Hide all
      </button>
    </div>
  );
}
